import React, { useState, useEffect, useCallback } from 'react';
import { useDataProvider } from 'react-admin';
import { logger, parseRemoteConfigValue } from '@musedrops/shared';
import type { RemoteConfigRow, RemoteConfigValueType } from '@musedrops/shared';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  TextField,
  IconButton,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Plus } from 'lucide-react';
import { LAYOUT } from '../../../theme';
import { SectionSaveButtons } from '../components/SectionSaveButtons';

const VALUE_TYPES: RemoteConfigValueType[] = ['bool', 'int', 'float', 'string', 'json'];

const KEY_REGEX = /^[a-z][a-z0-9-]{1,63}$/;

interface EditableRow extends RemoteConfigRow {
  /** Synthetic id (equals key) required by React-Admin */
  id: string;
  /** The pending edited value (only for inline value edits) */
  pendingValue?: string;
}

interface ConfigDialogState {
  open: boolean;
  mode: 'create' | 'edit';
  key: string;
  description: string;
  valueType: RemoteConfigValueType;
  value: string;
  originalKey?: string; // For edit mode — the key before changes
}

const emptyDialog: ConfigDialogState = {
  open: false,
  mode: 'create',
  key: '',
  description: '',
  valueType: 'string',
  value: '',
};

export const RemoteConfigSettings: React.FC = () => {
  const dataProvider = useDataProvider();
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [dialog, setDialog] = useState<ConfigDialogState>(emptyDialog);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const loadConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await dataProvider.getList('remote_configs', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'key', order: 'ASC' },
        filter: {},
      });
      // dataProvider adds synthetic `id: key` for remote_configs
      setRows(data as unknown as EditableRow[]);
    } catch (error) {
      logger.error('Failed to load remote configs', { error: String(error) });
      setNotification({ message: 'Failed to load configs', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [dataProvider]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  // ── Dirty state ──
  const hasChanges = rows.some((r) => r.pendingValue !== undefined);

  const handleValueChange = (key: string, newValue: string) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        // If value matches original, clear pending
        const pending = newValue === r.value ? undefined : newValue;
        return { ...r, pendingValue: pending };
      })
    );
  };

  const handleBoolToggle = (key: string, current: string) => {
    const next = current.toLowerCase() === 'true' ? 'false' : 'true';
    handleValueChange(key, next);
  };

  const handleDiscard = () => {
    setRows((prev) => prev.map((r) => ({ ...r, pendingValue: undefined })));
  };

  // ── Batch save ──
  const handleSave = async () => {
    const dirty = rows.filter((r) => r.pendingValue !== undefined);
    if (dirty.length === 0) return;

    // Validate before saving
    for (const row of dirty) {
      const parsed = parseRemoteConfigValue(row.pendingValue!, row.value_type);
      if (parsed === undefined) {
        setNotification({
          message: `Invalid value for "${row.key}" (type: ${row.value_type})`,
          type: 'error',
        });
        return;
      }
    }

    try {
      setSaving(true);
      await Promise.all(
        dirty.map((row) =>
          dataProvider.update('remote_configs', {
            id: row.key,
            data: { value: row.pendingValue },
            previousData: row,
          })
        )
      );
      setNotification({ message: 'Configs saved', type: 'success' });
      await loadConfigs();
    } catch (error) {
      logger.error('Failed to save remote configs', { error: String(error) });
      setNotification({ message: 'Failed to save', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Dialog ──
  const openEditDialog = (row: EditableRow) => {
    setDialog({
      open: true,
      mode: 'edit',
      key: row.key,
      description: row.description ?? '',
      valueType: row.value_type,
      value: row.pendingValue ?? row.value,
      originalKey: row.key,
    });
    setDialogError(null);
  };

  const openCreateDialog = () => {
    setDialog({ ...emptyDialog, open: true, mode: 'create' });
    setDialogError(null);
  };

  const closeDialog = () => {
    setDialog(emptyDialog);
    setDialogError(null);
  };

  const validateDialog = (): string | null => {
    if (!KEY_REGEX.test(dialog.key)) {
      return 'Key must be kebab-case (lowercase letters, numbers, hyphens), 2-64 chars';
    }
    // Uniqueness check (for create, or if key changed in edit)
    if (dialog.mode === 'create' || dialog.key !== dialog.originalKey) {
      if (rows.some((r) => r.key === dialog.key)) {
        return `Key "${dialog.key}" already exists`;
      }
    }
    const parsed = parseRemoteConfigValue(dialog.value, dialog.valueType);
    if (parsed === undefined) {
      return `Invalid value for type "${dialog.valueType}"`;
    }
    return null;
  };

  const handleDialogSave = async () => {
    const error = validateDialog();
    if (error) {
      setDialogError(error);
      return;
    }

    try {
      setSaving(true);
      if (dialog.mode === 'create') {
        await dataProvider.create('remote_configs', {
          data: {
            key: dialog.key,
            description: dialog.description || null,
            value_type: dialog.valueType,
            value: dialog.value,
          },
        });
        setNotification({ message: `Config "${dialog.key}" created`, type: 'success' });
      } else {
        // Edit mode — if key changed, create new first then delete old (safe order)
        if (dialog.originalKey && dialog.key !== dialog.originalKey) {
          await dataProvider.create('remote_configs', {
            data: {
              key: dialog.key,
              description: dialog.description || null,
              value_type: dialog.valueType,
              value: dialog.value,
            },
          });
          await dataProvider.delete('remote_configs', {
            id: dialog.originalKey,
            previousData: { id: dialog.originalKey, key: dialog.originalKey },
          });
        } else {
          await dataProvider.update('remote_configs', {
            id: dialog.key,
            data: {
              description: dialog.description || null,
              value_type: dialog.valueType,
              value: dialog.value,
            },
            previousData: { key: dialog.key },
          });
        }
        setNotification({ message: `Config "${dialog.key}" updated`, type: 'success' });
      }
      closeDialog();
      await loadConfigs();
    } catch (err) {
      logger.error('Failed to save config dialog', { error: String(err) });
      setDialogError('Failed to save. Check logs.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render value control ──
  const renderValueControl = (row: EditableRow) => {
    const currentValue = row.pendingValue ?? row.value;
    const isDirty = row.pendingValue !== undefined;

    switch (row.value_type) {
      case 'bool':
        return (
          <Switch
            checked={currentValue.toLowerCase() === 'true'}
            onChange={() => handleBoolToggle(row.key, currentValue)}
            disabled={saving}
            size="small"
          />
        );
      case 'int':
      case 'float':
        return (
          <TextField
            value={currentValue}
            onChange={(e) => handleValueChange(row.key, e.target.value)}
            disabled={saving}
            size="small"
            type="number"
            inputProps={row.value_type === 'int' ? { step: 1 } : { step: 'any' }}
            sx={{ width: 140, ...(isDirty && { '& .MuiOutlinedInput-root': { borderColor: 'warning.main' } }) }}
          />
        );
      case 'json':
        return (
          <TextField
            value={currentValue}
            onChange={(e) => handleValueChange(row.key, e.target.value)}
            disabled={saving}
            size="small"
            multiline
            maxRows={3}
            sx={{ width: 280, ...(isDirty && { '& .MuiOutlinedInput-root': { borderColor: 'warning.main' } }) }}
          />
        );
      default: // string
        return (
          <TextField
            value={currentValue}
            onChange={(e) => handleValueChange(row.key, e.target.value)}
            disabled={saving}
            size="small"
            sx={{ width: 280, ...(isDirty && { '& .MuiOutlinedInput-root': { borderColor: 'warning.main' } }) }}
          />
        );
    }
  };

  // ── Render dialog value control ──
  const renderDialogValueControl = () => {
    switch (dialog.valueType) {
      case 'bool':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Switch
              checked={dialog.value.toLowerCase() === 'true'}
              onChange={() =>
                setDialog((d) => ({
                  ...d,
                  value: d.value.toLowerCase() === 'true' ? 'false' : 'true',
                }))
              }
            />
            <Typography variant="body2">
              {dialog.value.toLowerCase() === 'true' ? 'true' : 'false'}
            </Typography>
          </Box>
        );
      case 'json':
        return (
          <TextField
            label="Value"
            value={dialog.value}
            onChange={(e) => setDialog((d) => ({ ...d, value: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            margin="normal"
          />
        );
      case 'int':
      case 'float':
        return (
          <TextField
            label="Value"
            value={dialog.value}
            onChange={(e) => setDialog((d) => ({ ...d, value: e.target.value }))}
            fullWidth
            type="number"
            inputProps={dialog.valueType === 'int' ? { step: 1 } : { step: 'any' }}
            margin="normal"
          />
        );
      default:
        return (
          <TextField
            label="Value"
            value={dialog.value}
            onChange={(e) => setDialog((d) => ({ ...d, value: e.target.value }))}
            fullWidth
            margin="normal"
          />
        );
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3, maxWidth: LAYOUT.mediumContentWidth, margin: '0 auto' }}>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Value</TableCell>
              <TableCell align="center">
                <IconButton onClick={openCreateDialog} color="primary" size="small">
                  <Plus size={18} />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No remote configs yet. Click + to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.key} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {row.key}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => navigator.clipboard.writeText(row.key)}
                        sx={{ opacity: 0.3, '&:hover': { opacity: 1 }, p: 0.25 }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                    {row.description && (
                      <Typography variant="caption" color="text.secondary">
                        {row.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={row.value_type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{renderValueControl(row)}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => openEditDialog(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <SectionSaveButtons
        hasChanges={hasChanges}
        saving={saving}
        onSave={handleSave}
        onDiscard={handleDiscard}
        hideWhenNoChanges={false}
      />

      {/* Create / Edit Dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog.mode === 'create' ? 'Add Config' : 'Edit Config'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Changing a key or type may break clients that depend on it.
          </Alert>

          <TextField
            label="Key"
            value={dialog.key}
            onChange={(e) => setDialog((d) => ({ ...d, key: e.target.value }))}
            fullWidth
            margin="normal"
            helperText="kebab-case (e.g. inserts-enabled)"
            inputProps={{ pattern: '^[a-z][a-z0-9-]{1,63}$' }}
          />

          <TextField
            label="Description"
            value={dialog.description}
            onChange={(e) => setDialog((d) => ({ ...d, description: e.target.value }))}
            fullWidth
            margin="normal"
          />

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={dialog.valueType}
              label="Type"
              onChange={(e) =>
                setDialog((d) => ({
                  ...d,
                  valueType: e.target.value as RemoteConfigValueType,
                  // Reset value when type changes — default bool to 'false'
                  value: e.target.value === d.valueType
                    ? d.value
                    : e.target.value === 'bool' ? 'false' : '',
                }))
              }
            >
              {VALUE_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {renderDialogValueControl()}

          {dialogError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleDialogSave} disabled={saving}>
            {saving ? <CircularProgress size={16} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={() => setNotification(null)}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.type}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
